import { Input } from '@breetometer/components';
import {
	DEFAULT_INPUT_MAX_LENGTH,
	filterDisabledNetwork,
	getNetworkFee,
	type IDropDownItem,
	type NetworkType,
	type TokenType,
} from 'BreetHelpers';
import { useSavedWalletAddressApi } from 'BreetHooks';
import { memo, type MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';

import DropDown from '../Dropdown/Dropdown';
import DropdownItemNetwork from './dropDownItem/DropdownItem.Network';
import DropdownItemToken from './dropDownItem/DropdownItem.Token';
import type { IWalletAddressFormComponentProps } from './WalletAddressForm.helpers';

const WalletAddressFormInputComponent = ({
	tokenOptions,
	walletInfoData,
	authSettings,
	formDataContextProps,
}: IWalletAddressFormComponentProps) => {
	const { isSavedWalletAddressesEmpty, getSavedWalletAddress, isLoadingSavedWalletAddresses } = useSavedWalletAddressApi();

	const [selectedTokenProps, setSelectedTokenProps] = useState<IDropDownItem | undefined>(undefined);
	const [selectedNetworkProps, setSelectedNetworkProps] = useState<IDropDownItem | undefined>(undefined);

	const {
		hookFormProps: { register, watch, setValue },
		getFieldError,
		setValueAndValidate,
	} = formDataContextProps;

	const formData = watch();
	const selectedToken = formData.token as TokenType | undefined;
	const selectedNetwork = formData.network as NetworkType | undefined;
	const receiverAddress = formData.receiverAddress as string | undefined;
	const walletName = formData.walletName as string | undefined;

	const networks = useMemo(() => {
		try {
			if (!selectedToken) return [];

			return walletInfoData?.data?.[selectedToken].networks ?? [];
		} catch {
			console.error('Networks not found');
			return [];
		}
	}, [selectedToken, walletInfoData?.data]);

	const networkOptions: IDropDownItem[] = useMemo(() => {
		try {
			if (!selectedToken) return [];

			const usdWithdrawalMeta = authSettings?.usdWithdrawalMeta;

			if (!usdWithdrawalMeta) return [];

			return networks.filter(filterDisabledNetwork(usdWithdrawalMeta, selectedToken)).map((option) => ({
				value: option.network,
				icon: option.avatar,
				code: `$ ${getNetworkFee(selectedToken, option.network, usdWithdrawalMeta)}`,
				name: option.network,
				id: option.network,
			}));
		} catch {
			console.error('Network options not found');
			return [];
		}
	}, [authSettings?.usdWithdrawalMeta, networks, selectedToken]);

	useEffect(() => {
		if (selectedToken && !selectedTokenProps) {
			setSelectedTokenProps(tokenOptions.find((i) => i.value === selectedToken));
		}

		if (selectedNetwork && !selectedNetworkProps) {
			setSelectedNetworkProps(networkOptions.find((i) => i.value === selectedNetwork));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedToken, selectedNetwork, tokenOptions, networkOptions]);

	const handleInputSelect = useCallback(
		(key: 'token' | 'network') => (valueArg: IDropDownItem) => (e: MouseEvent<HTMLButtonElement>) => {
			e.preventDefault();

			if (key === 'token') setSelectedTokenProps(valueArg);

			if (key === 'network') setSelectedNetworkProps(valueArg);

			setValueAndValidate(key, valueArg.value).catch(console.error);

			if (key === 'token') {
				setValue('network', '');
				setValue('receiverAddress', '');
				setValue('walletName', '');
				setSelectedNetworkProps(undefined);
			}

			if (key === 'network' && !!receiverAddress) {
				setValueAndValidate('receiverAddress', receiverAddress).catch(console.error);
			}
		},
		[setValueAndValidate, receiverAddress, setValue]
	);

	const hasReceiverAddressError = !!getFieldError('receiverAddress')?.message;

	const derivedWalletName = useMemo(() => {
		if (isSavedWalletAddressesEmpty || hasReceiverAddressError || !receiverAddress) {
			return '';
		}

		if (walletName) return walletName;

		return getSavedWalletAddress({ address: receiverAddress, token: selectedToken })?.label ?? '';
	}, [isSavedWalletAddressesEmpty, hasReceiverAddressError, receiverAddress, walletName, getSavedWalletAddress, selectedToken]);

	useEffect(() => {
		if (!receiverAddress || (!derivedWalletName && !isLoadingSavedWalletAddresses)) {
			setValue('walletName', '', { shouldDirty: false });
		} else if (derivedWalletName) {
			setValue('walletName', derivedWalletName, { shouldDirty: true });
		}
	}, [derivedWalletName, isLoadingSavedWalletAddresses, receiverAddress, setValue]);

	return (
		<>
			<DropDown.Modern
				items={tokenOptions}
				label='Select Coin'
				searchItemPlaceholder='Search asset'
				id='select-funding-method'
				CustomItemOption={DropdownItemToken}
				errorMessage={
					!walletInfoData || !authSettings?.usdWithdrawalMeta || tokenOptions.length
						? getFieldError('token')?.message
						: 'No coin is currently available. Please try again later or contact support.'
				}
				selectedItem={selectedTokenProps}
				handleItemSelect={handleInputSelect('token')}
			/>

			{!!selectedToken && (
				<>
					<DropDown.Modern
						items={networkOptions}
						label='Destination Network'
						searchItemPlaceholder='Search Network'
						id='select-funding-network'
						CustomItemOption={DropdownItemNetwork}
						errorMessage={
							!walletInfoData || !authSettings?.usdWithdrawalMeta || networkOptions.length
								? getFieldError('network')?.message
								: 'No network is currently available. Please try again later or contact support.'
						}
						selectedItem={selectedNetworkProps}
						handleItemSelect={handleInputSelect('network')}
					/>

					<Input
						label="Enter Receiver's Address"
						id='receiverAddress'
						required
						widthType='full'
						autoComplete='off'
						placeholder=' '
						maxLength={128}
						{...register('receiverAddress')}
						errorMessage={getFieldError('receiverAddress')?.message}
					/>
					<div className='wallet-address-input'>
						<Input
							label='Wallet Name e.g. Binance Wallet'
							id='walletAddress'
							required
							widthType='full'
							autoComplete='off'
							placeholder=' '
							maxLength={DEFAULT_INPUT_MAX_LENGTH}
							{...register('walletName')}
							errorMessage={getFieldError('walletName')?.message}
						/>
					</div>
				</>
			)}
		</>
	);
};

const WalletAddressFormInput = memo(WalletAddressFormInputComponent);

WalletAddressFormInput.displayName = 'WalletAddressFormInput';

export default WalletAddressFormInput;
